import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { ArrowLeft, Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { AuthLayout } from './AuthLayout'

// Hint simples de forca da senha (comprimento + variedade de caracteres).
function passwordStrength(password: string): { label: string; className: string } | null {
  if (!password) return null
  let score = 0
  if (password.length >= 8) score++
  if (password.length >= 12) score++
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++
  if (/\d/.test(password)) score++
  if (/[^A-Za-z0-9]/.test(password)) score++
  if (score <= 1) return { label: 'Senha fraca', className: 'text-error' }
  if (score <= 3) return { label: 'Senha razoável', className: 'text-warning' }
  return { label: 'Senha forte', className: 'text-success' }
}

export function PasswordResetScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const strength = passwordStrength(newPassword)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newPassword || !confirmPassword) {
      toast.error('Preencha todos os campos')
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem')
      return
    }
    setLoading(true)
    try {
      const res = await apiService.resetPassword(token, newPassword)
      toast.success(res.message)
      setSuccess(true)
      setTimeout(() => navigate('/login'), 3000)
    } catch (err: any) {
      toast.error(err.message || 'Erro ao redefinir senha')
    } finally {
      setLoading(false)
    }
  }

  const backToLogin = (
    <button
      type="button"
      onClick={() => navigate('/login')}
      className="mt-6 flex w-full cursor-pointer items-center justify-center gap-2 border-none bg-transparent p-0 text-sm text-link hover:underline"
    >
      <ArrowLeft className="h-4 w-4" />
      Voltar ao login
    </button>
  )

  if (!token) {
    return (
      <AuthLayout>
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm sm:p-8">
          <h2 className="text-xl font-bold">Redefinir senha</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Link inválido ou expirado. Solicite um novo em "Esqueci minha senha".
          </p>
          {backToLogin}
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="rounded-lg border border-border bg-card p-6 shadow-sm sm:p-8">
        {success ? (
          <div className="text-center">
            <h2 className="text-xl font-bold">Senha redefinida</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Tudo certo. Redirecionando para o login...
            </p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-bold">Nova senha</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Escolha a nova senha da sua conta.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">Nova senha</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    autoFocus
                    autoComplete="new-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    className="absolute inset-y-0 right-2 flex items-center px-1 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(s => !s)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {strength && (
                  <p className={`text-xs font-medium ${strength.className}`} aria-live="polite">
                    {strength.label}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirmar nova senha</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar nova senha'}
              </Button>
            </form>
          </>
        )}
        {!success && backToLogin}
      </div>
    </AuthLayout>
  )
}
